import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../network/ApiClient';
import { useAuth } from '../context/AuthContext';

/**
 * 'loading'    — /me in flight (or between bounded retries); "don't know yet".
 * 'complete'   — has a backend username.
 * 'incomplete' — Firebase-authenticated but no username (Step 2 unfinished).
 * 'error'      — bounded retries exhausted; the guard shows a Retry UI.
 *
 * Guards MUST treat 'loading' and 'error' as "unknown" — never protected
 * content, never a redirect based on either.
 */
export type RegistrationStatus = 'loading' | 'complete' | 'incomplete' | 'error';

interface Snapshot {
  status:   RegistrationStatus;
  username: string | null;
}

export interface RegistrationStatusResult extends Snapshot {
  /** Start a fresh attempt sequence (invalidates any in-flight stale fetch). */
  retry: () => void;
}

// Tunables — small, fixed, bounded. 1 initial attempt + (MAX_ATTEMPTS-1)
// retries, each separated by RETRY_DELAY_MS.
export const MAX_ATTEMPTS  = 3;
export const RETRY_DELAY_MS = 500;

const LOADING: Snapshot = { status: 'loading', username: null };

// Module cache holds only a SUCCESSFUL result, reused across mounts so
// navigating between protected routes doesn't refetch. `epoch` invalidates
// any in-flight fetch on logout/account switch (and on manual retry) so a
// stale response can never land on the next identity.
let cached: Snapshot | null = null;
let epoch = 0;

/** Cleared centrally on logout/account switch (AuthContext) and after a
 *  successful username save, so a stale status never lingers and account A's
 *  status can never be observed by account B. */
export function clearRegistrationStatusCache(): void {
  epoch++;
  cached = null;
}

export function useRegistrationStatus(): RegistrationStatusResult {
  const { isLoggedIn, identityKey } = useAuth();
  const [snapshot, setSnapshot] = useState<Snapshot>(cached ?? LOADING);
  const [nonce, setNonce] = useState(0); // bump to force a fresh attempt sequence

  useEffect(() => {
    if (!isLoggedIn) { setSnapshot(LOADING); return; }
    if (cached)      { setSnapshot(cached); return; }

    let cancelled = false;
    const myEpoch = epoch;
    let timer: ReturnType<typeof setTimeout> | undefined;

    setSnapshot(LOADING); // never leak a previous identity's status while (re)fetching

    const stale = () => cancelled || myEpoch !== epoch;

    const attempt = async (n: number): Promise<void> => {
      try {
        const me = await apiClient.getMe();
        if (stale()) return; // logout/switch/retry happened mid-flight — drop it
        cached = {
          status:   me.registration_complete ? 'complete' : 'incomplete',
          username: me.username,
        };
        setSnapshot(cached);
      } catch {
        if (stale()) return;
        if (n < MAX_ATTEMPTS) {
          // Bounded automatic retry after a short delay.
          timer = setTimeout(() => { if (!stale()) void attempt(n + 1); }, RETRY_DELAY_MS);
        } else {
          // Exhausted — fail closed into an explicit error state (never a
          // guessed complete/incomplete). The guard renders Retry.
          setSnapshot({ status: 'error', username: null });
        }
      }
    };

    void attempt(1);

    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identityKey, nonce]);

  const retry = useCallback(() => {
    epoch++;      // invalidate any in-flight stale attempt from the failed sequence
    cached = null;
    setNonce(n => n + 1); // re-run the effect → fresh attempt sequence
  }, []);

  const value = isLoggedIn ? snapshot : LOADING;
  return { ...value, retry };
}
